/**
 * TriplanarMappingPlugin
 * ----------------------
 * A `MaterialPluginBase` that extends a `StandardMaterial` with a triplanar
 * texture-sampling path. Activated by the coordinator when a mesh's chosen
 * mapping mode resolves to `triplanar` (either explicitly or via Auto mode
 * when the mesh's UV data is missing/broken).
 *
 * Why a plugin (not a replacement material)?
 *   • Keeps PBR/StandardMaterial lighting, shadows, projection-shader
 *     integration — we only override the UV used to sample `diffuseTexture`.
 *   • Zero geometry modification, zero material duplication.
 *   • Shader-side only; no per-frame CPU cost beyond a uniform update.
 *
 * Algorithm (in the fragment shader):
 *   1. Normalize |vNormalW| to get per-axis weights.
 *   2. Sample diffuseSampler three times with world-space XY / YZ / XZ slices.
 *   3. Blend the three samples by the normal weights.
 *   → No seams, no stretching, works on any mesh topology.
 */
import * as BABYLON from '@babylonjs/core';

export const TRIPLANAR_PLUGIN_NAME = 'TriplanarMapping';

export class TriplanarMappingPlugin extends BABYLON.MaterialPluginBase {
  _isEnabled = false;
  _scale = 1.0;

  constructor(material) {
    // 200 priority = after the material's main shading passes have computed
    // diffuseColor; we then overwrite it with triplanar samples.
    super(material, TRIPLANAR_PLUGIN_NAME, 200, { TRIPLANAR_MAPPING: false });
  }

  /**
   * Declare compatibility with both GLSL (WebGL2) and WGSL (WebGPU) so the
   * plugin can be attached regardless of the engine type. Only the GLSL path
   * actually injects shader code today — on WebGPU the plugin gracefully
   * no-ops (see `getCustomCode`) and the material continues to sample with
   * standard UVs. A full WGSL implementation is v1.1.
   * planned for v1.1 once the GLSL path has proven out in production.
   * v1.1 (May 2026): WGSL fragment snippet added — parity achieved across
   * WebGL2 and WebGPU. Plugin now activates on both backends.
   */
  isCompatible(shaderLanguage) {
    // Babylon's ShaderLanguage enum: GLSL = 0, WGSL = 1
    return shaderLanguage === 0 || shaderLanguage === 1;
  }

  get isEnabled() { return this._isEnabled; }
  set isEnabled(value) {
    if (this._isEnabled === value) return;
    this._isEnabled = value;
    this.markAllDefinesAsDirty();
    this._enable(value);
  }

  get scale() { return this._scale; }
  set scale(value) { this._scale = Math.max(0.001, Number(value) || 1.0); }

  prepareDefines(defines) {
    defines.TRIPLANAR_MAPPING = this._isEnabled;
  }

  getClassName() { return 'TriplanarMappingPlugin'; }

  getUniforms(shaderLanguage) {
    // UBO declaration is the same for both GLSL and WGSL — Babylon generates
    // the right struct/block binding per backend.
    const ubo = [{ name: 'triplanarScale', size: 1, type: 'float' }];
    if (shaderLanguage === 1 /* WGSL */) {
      // WGSL auto-generates the uniform block from `ubo`; no extra code needed.
      return { ubo };
    }
    return {
      ubo,
      fragment:
        '#ifdef TRIPLANAR_MAPPING\n' +
        'uniform float triplanarScale;\n' +
        '#endif',
    };
  }

  bindForSubMesh(uniformBuffer) {
    if (this._isEnabled) {
      uniformBuffer.updateFloat('triplanarScale', this._scale);
    }
  }

  getCustomCode(shaderType, shaderLanguage) {
    if (shaderType === 'vertex') {
      // StandardMaterial already exports `vPositionW` / `vNormalW` as
      // varyings whenever NORMAL is defined — no vertex-shader injection.
      return {};
    }

    // IMPORTANT: in StandardMaterial's fragment shader the sampled diffuse
    // texture is stored in `baseColor.rgb` (vec4). `diffuseColor` at the
    // injection point is the per-material tint (vDiffuseColor.rgb) and it
    // gets MULTIPLIED into `baseColor.rgb` further down. We overwrite
    // `baseColor.rgb` with the triplanar blend so the tint + lighting +
    // projection stages all continue to apply unchanged.

    if (shaderLanguage === 1 /* WGSL */) {
      return {
        CUSTOM_FRAGMENT_UPDATE_DIFFUSE:
          '#ifdef TRIPLANAR_MAPPING\n' +
          '#ifdef DIFFUSE\n' +
          '  let triN_w = abs(normalize(fragmentInputs.vNormalW));\n' +
          '  let triNp = pow(triN_w, vec3f(4.0));\n' +
          '  let triNn = triNp / max(triNp.x + triNp.y + triNp.z, 0.00001);\n' +
          '  let uvX_tri = fragmentInputs.vPositionW.zy * uniforms.triplanarScale;\n' +
          '  let uvY_tri = fragmentInputs.vPositionW.xz * uniforms.triplanarScale;\n' +
          '  let uvZ_tri = fragmentInputs.vPositionW.xy * uniforms.triplanarScale;\n' +
          '  let cX = textureSample(diffuseSampler, diffuseSamplerSampler, uvX_tri);\n' +
          '  let cY = textureSample(diffuseSampler, diffuseSamplerSampler, uvY_tri);\n' +
          '  let cZ = textureSample(diffuseSampler, diffuseSamplerSampler, uvZ_tri);\n' +
          '  let triBlend = cX.rgb * triNn.x + cY.rgb * triNn.y + cZ.rgb * triNn.z;\n' +
          '  baseColor = vec4f(triBlend, baseColor.a);\n' +
          '#endif\n' +
          '#endif',
      };
    }

    // GLSL (WebGL2) path
    return {
      CUSTOM_FRAGMENT_UPDATE_DIFFUSE:
        '#ifdef TRIPLANAR_MAPPING\n' +
        '#ifdef DIFFUSE\n' +
        '  vec3 triN = abs(normalize(vNormalW));\n' +
        // Sharpen blend so each axis wins clearly — prevents grey-mush blending.
        '  triN = pow(triN, vec3(4.0));\n' +
        '  triN = triN / max(triN.x + triN.y + triN.z, 0.00001);\n' +
        '  vec2 uvX_tri = vPositionW.zy * triplanarScale;\n' +
        '  vec2 uvY_tri = vPositionW.xz * triplanarScale;\n' +
        '  vec2 uvZ_tri = vPositionW.xy * triplanarScale;\n' +
        '  vec4 cX = texture2D(diffuseSampler, uvX_tri);\n' +
        '  vec4 cY = texture2D(diffuseSampler, uvY_tri);\n' +
        '  vec4 cZ = texture2D(diffuseSampler, uvZ_tri);\n' +
        // Overwrite baseColor.rgb (the UV-sampled texture) with the triplanar
        // blend. `diffuseColor` is the material tint — leaving it alone means
        // user-set colors, lighting, and projection all keep working.
        '  baseColor.rgb = cX.rgb * triN.x + cY.rgb * triN.y + cZ.rgb * triN.z;\n' +
        '#endif\n' +
        '#endif',
    };
  }
}

/**
 * Ensure the given StandardMaterial has a triplanar plugin attached.
 * Returns the plugin instance (creates one on first call).
 * Safe to call repeatedly — plugin is idempotent. Any failure to attach
 * (e.g. engine using WGSL in a version that rejected early plugins) is
 * swallowed and returns null so callers never propagate engine errors.
 */
export function ensureTriplanarPlugin(material) {
  if (!material || !(material instanceof BABYLON.StandardMaterial)) return null;
  const existing = material.pluginManager && material.pluginManager.getPlugin
    ? material.pluginManager.getPlugin(TRIPLANAR_PLUGIN_NAME)
    : null;
  if (existing) return existing;
  try {
    return new TriplanarMappingPlugin(material);
  } catch (e) {
    // WebGPU / WGSL engines without full plugin-GLSL support will reject
    // the plugin at construction time. Log once and return null — the
    // coordinator will then keep the mesh in UV mode (graceful fallback).
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[TriplanarMappingPlugin] attach failed — staying in UV mode:', e && e.message);
    }
    return null;
  }
}
